import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PushDevicesDataService, PushDevicesColumnsService } from '../../services';
import { DialogPushDeviceFormComponent } from './push-device-form/push-device-form.component';
import { DialogPushDeviceDetailsComponent } from './push-device-details/push-device-details.component';
import { PushDevice, PushDevicesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-push-devices',
  templateUrl: './push-devices.component.html',
  styleUrls: ['./push-devices.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PushDevicesComponent implements OnInit, OnDestroy {

  private pushDeviceAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushDeviceEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushDeviceDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public pushDeviceToDelete: PushDevice = {};
  public pushDevices: PushDevice[] = [];
  public pushDevice: PushDevice = {};
  public selectedPushDevices: PushDevice[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private pushDevicesDataService: PushDevicesDataService,
    private pushDevicesColumnsService: PushDevicesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PushDevicesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Push Devices - Management');
    this.primengConfig.ripple = true;
    this.getPushDevices();
  }

  ngOnDestroy(): void {
    console.log('PushDevicesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPushDevices(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.pushDevicesDataService.getPushDevices(this.parameters).subscribe((response: PushDevicesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.pushDevices = response.success.data;
        } else {
          this.pushDevices = this.pushDevices.concat(response.success.data);
        }
        if (response.inlineCount > this.pushDevices.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPushDevices();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHDEVICESNOTLOADED')});
    });
  }

  importPushDevices(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.pushDevicesDataService.addPushDevices(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHDEVICESCREATED')});
                if (proceed === j) {
                  this.getPushDevices();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHDEVICESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHDEVICESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.pushDevicesDataService.updatePushDevices(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHDEVICESUPDATED')});
                if (proceed === j) {
                  this.getPushDevices();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHDEVICESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHDEVICESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.pushDevicesColumnsService.getColumns();
    console.log('col', this.columns)

    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('pushDevices.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPushDevices() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Push Devices?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.pushDevicesDataService.deletePushDevices(this.selectedPushDevices).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Push Devices Deleted', life: 3000});
            this.getPushDevices();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Devices not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Devices not deleted'});
        });
        this.selectedPushDevices = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.pushDevices, this.columns, 'PushDevices');
  }

  exportExcel() {
    ToolsService.exportExcel(this.pushDevices, this.columns, 'PushDevices');
  }

  exportCsv() {
    ToolsService.exportCsv(this.pushDevices, this.columns, 'PushDevices');
  }

  refresh() {
    this.pushDevicesDataService.clearCache();
    this.getPushDevices();
  }

  pushDeviceAdd(): void {
    if (!this.navigation.getParamValue('pushDevices.addInDialog', true)) {
      this.router.navigate(['/push-devices/add']);
      return;
    }
    this.pushDeviceAddDialog = this.dialogService.open(DialogPushDeviceFormComponent, {
      header: '', // this.translate.instant('NEWPUSHDEVICE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        pushDevice: {id: null},
        users: this.pushDevicesColumnsService.users,
      },
    });

    this.pushDeviceAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'PushDevice added'});
        this.getPushDevices();
      }
    });
  }

  pushDeviceEdit(pushDevice: PushDevice): void {
    if (!this.navigation.getParamValue('pushDevices.editInDialog', true)) {
      this.router.navigate(['/push-devices/edit/' + pushDevice.id]);
      return;
    }
    this.pushDeviceEditDialog = this.dialogService.open(DialogPushDeviceFormComponent, {
      header: '', // this.translate.instant('EDITPUSHDEVICE') + ' ' + pushDevice.appName,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        pushDevice: pushDevice,
        users: this.pushDevicesColumnsService.users,
      },
    });

    this.pushDeviceEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'PushDevice updated'});
        this.getPushDevices();
      }
    });
  }

  pushDeviceDetails(pushDevice: PushDevice): void {
    if (!this.navigation.getParamValue('pushDevices.detailsInDialog', true)) {
      this.router.navigate(['/push-devices/details/' + pushDevice.id]);
      return;
    }
    this.pushDeviceDetailsDialog = this.dialogService.open(DialogPushDeviceDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        pushDevice: pushDevice,
      }
    });
  }

  pushDeviceDelete(pushDevice: PushDevice): void {
    this.pushDeviceToDelete = pushDevice;
    this.messageService.clear();
    this.messageService.add({
      key: 'pushDeviceDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + pushDevice.appName + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.pushDevicesDataService.deletePushDevice(this.pushDeviceToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Push Device deleted'});
      this.getPushDevices();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Device not deleted'});
    });
  }

  onDeleteReject(): void {
    this.pushDeviceToDelete = {};
    this.messageService.clear('pushDeviceDelete');
  }

}
